import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PropertyFilters, SortOption } from "@/components/find/PropertyFilters";
import {
  fromSearchParams,
  readStoredSearch,
  writeStoredSearch,
} from "@/lib/property-search-params";

interface FindSearchParams {
  /** The search that was actually run. `null` means demo mode. */
  committedFilters: PropertyFilters | null;
  sortBy: SortOption;
  /** Serialized committed search — URL params, or the resumed session value. */
  searchParamsKey: string;
  setSearchParams: ReturnType<typeof useSearchParams>[1];
}

/**
 * Owns "what search is the Find page showing".
 *
 * The URL is the source of truth so refresh, back/forward and shared links work.
 * But navigating to another route and back lands on a bare `/deals`, which the URL
 * alone can't distinguish from a first visit — so the last committed search is
 * also mirrored into sessionStorage and resumed from there.
 *
 * Precedence: URL params → this session's last search → the default location.
 */
export function useFindSearchParams(): FindSearchParams {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlParamsKey = searchParams.toString();

  // Read once on mount. A later write must not change what we resumed from.
  const [storedParamsKey] = useState(readStoredSearch);

  /**
   * Resolved during render, not in an effect. An effect would let the first pass
   * run with the default location — firing a request for it — and only then
   * switch to the resumed search, costing a second request every time the user
   * comes back to the page.
   */
  const searchParamsKey = urlParamsKey || storedParamsKey;

  const { filters: committedFilters, sortBy } = useMemo(
    () => fromSearchParams(new URLSearchParams(searchParamsKey)),
    [searchParamsKey],
  );

  // Remember every committed search. Guarded on being non-empty so the
  // pre-restore first render can't wipe what we are about to resume.
  useEffect(() => {
    if (searchParamsKey) {
      writeStoredSearch(searchParamsKey);
    }
  }, [searchParamsKey]);

  // Reflect a resumed search in the URL so refreshing and sharing keep working.
  // `replace` because resuming isn't a navigation the back button should undo.
  useEffect(() => {
    if (!urlParamsKey && storedParamsKey) {
      setSearchParams(new URLSearchParams(storedParamsKey), { replace: true });
    }
  }, [urlParamsKey, storedParamsKey, setSearchParams]);

  return { committedFilters, sortBy, searchParamsKey, setSearchParams };
}
